import { Rectangle, Sprite, Texture, SCALE_MODES, Renderer } from "pixi.js"
import { getRendererApp } from "../renderer_app"
import { analyzeTreeArt, breakUpCrown, crownBreakupOrders, fellTreeArt, notchTreeArt, TreeArt } from "./tree_art"
import { FelledTree } from "./tree_fall"

interface Piece { source: Texture; x: number; y: number; width: number; height: number }
interface ArtImage {
    art: TreeArt; pixels: Uint8ClampedArray; pieces: Piece[]; stages: Map<string, Texture[]>
    // The art cut in two for a fall, one per side it can fall to. Built the first time a tree of this kind falls that way.
    felled: Map<number, FelledTree>
}

// How many steps a fallen crown breaks up in, the last being gone.
const BREAKUP_STAGES = 7

/** Read pristine composite art once, then share a small set of trunk-notch stages across trees. */
export class TreeDamage {
    private ids = new WeakMap<Texture, number>()
    private nextId = 0
    private textureId = (texture: Texture): number => {
        if (!this.ids.has(texture)) this.ids.set(texture, ++this.nextId)
        return this.ids.get(texture)!
    }

    private originals = new WeakMap<Texture, Texture>()
    private images = new WeakMap<Texture, Map<string, ArtImage | null>>()

    private image = (sprites: Sprite[]): ArtImage | undefined => {
        const set = sprites.filter(s => s && !s.destroyed && s.transform)
        if (!set.length) return undefined
        const pivot = Math.max(...set.map(s => s.y + s.texture.height))
        const xs = set.map(s => s.x + Math.sin(s.skew.x) * (pivot - s.y))
        const left = Math.min(...xs)
        const top = Math.min(...set.map(s => s.y))
        const pieces = set.map((s, i) => ({ source: this.originals.get(s.texture) || s.texture,
            x: Math.round(xs[i] - left), y: Math.round(s.y - top), width: s.texture.width, height: s.texture.height }))
        const key = pieces.map(p => `${this.textureId(p.source)}:${p.x}:${p.y}`).join("|")
        let layouts = this.images.get(pieces[0].source)
        if (!layouts) { layouts = new Map(); this.images.set(pieces[0].source, layouts) }
        if (layouts.has(key)) return layouts.get(key) || undefined
        try {
            const width = Math.max(...pieces.map(p => p.x + p.width))
            const height = Math.max(...pieces.map(p => p.y + p.height))
            const pixels = new Uint8ClampedArray(width * height * 4)
            for (const p of pieces) {
                const sample = new Sprite(p.source)
                let rgba: Uint8Array
                try { rgba = (getRendererApp().renderer as Renderer).extract.pixels(sample) }
                finally { sample.destroy() }
                if (rgba.length !== p.width * p.height * 4) throw new Error("Unexpected tree texture resolution")
                for (let y = 0; y < p.height; y++) {
                    pixels.set(rgba.subarray(y * p.width * 4, (y + 1) * p.width * 4), ((p.y + y) * width + p.x) * 4)
                }
            }
            const art = analyzeTreeArt(pixels, width, height)
            if (!art) throw new Error("No readable tree silhouette")
            const image = { pixels, pieces, art, stages: new Map<string, Texture[]>(), felled: new Map<number, FelledTree>() }
            layouts.set(key, image)
            return image
        } catch (_) {
            // Headless renderers and unreadable textures still get recoil and particles; never holes.
            layouts.set(key, null)
            return undefined
        }
    }

    art = (sprites: Sprite[]): TreeArt | undefined => this.image(sprites)?.art

    /** The tree cut through for a fall toward `side`, as textures the size of the whole art. */
    felled = (sprites: Sprite[], side: number): FelledTree | undefined => {
        const image = this.image(sprites)
        if (!image) return undefined
        const fall = side < 0 ? -1 : 1
        let felled = image.felled.get(fall)
        if (!felled) {
            const { width, height } = image.art
            const cut = fellTreeArt(image.pixels, width, height, image.art, fall)
            const texture = (rgba: Uint8ClampedArray) =>
                Texture.fromBuffer(new Uint8Array(rgba), width, height, { scaleMode: SCALE_MODES.NEAREST })
            // The break-up is baked a stage at a time as the animation reaches it, and only the first
            // time a tree of this kind clears away: one frame never pays for all of it.
            let orders: Float32Array | undefined
            const stages: Texture[] = []
            felled = { art: image.art, cutY: cut.cutY, hingeX: cut.hingeX, crown: texture(cut.crown),
                stump: texture(cut.stump), shadow: cut.shadow && texture(cut.shadow),
                breakup: { stages: BREAKUP_STAGES - 1, stage: (i: number) => {
                    if (!stages[i]) {
                        orders = orders || crownBreakupOrders(width, height, cut.hingeX, cut.cutY)
                        stages[i] = texture(breakUpCrown(cut.crown, orders, (i + 1) / BREAKUP_STAGES))
                    }
                    return stages[i]
                } } }
            image.felled.set(fall, felled)
        }
        return felled
    }

    apply = (sprites: Sprite[], damage: number, side: number) => {
        const image = this.image(sprites)
        if (!image) return
        const stage = Math.max(0, Math.min(10, Math.round(damage * 10)))
        if (!stage) {
            sprites.forEach(s => { s.texture = this.originals.get(s.texture) || s.texture })
            return
        }
        const key = `${stage}:${side < 0 ? -1 : 1}`
        let textures = image.stages.get(key)
        if (!textures) {
            const { width, height } = image.art
            const rgba = notchTreeArt(image.pixels, width, height, image.art, stage / 10, side)
            const atlas = Texture.fromBuffer(new Uint8Array(rgba), width, height, { scaleMode: SCALE_MODES.NEAREST })
            textures = image.pieces.map(p => new Texture(atlas.baseTexture, new Rectangle(p.x, p.y, p.width, p.height)))
            textures.forEach((texture, i) => this.originals.set(texture, image.pieces[i].source))
            image.stages.set(key, textures)
        }
        sprites.forEach((s, i) => { if (textures[i]) s.texture = textures[i] })
    }
}
